import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as Popover from '@radix-ui/react-popover';
import { Icon } from '@iconify/react';
import shieldAlt from '@iconify/icons-fa-solid/shield-alt';
import { useTranslation } from 'react-i18next';
import { getUID, getSocket } from '@/utils';
import { C } from '@/utils/constants';
import Cookies from 'js-cookie';
import { useScreenSize } from '@/hooks';
import LogoutModal from './LogoutModal';
import styles from './ProfileMenu.module.css';

const DEFAULT_AVATAR_PATH = '/assets/images/profile-jk.webp';

/**
 * ProfileMenu Component
 * Revamped profile dropdown menu using Radix UI Popover
 * Matches design system color scheme and mobile responsive
 */
const ProfileMenu = () => {
  const { t } = useTranslation();
  const [avatarUrl, setAvatarUrl] = useState(DEFAULT_AVATAR_PATH);
  const [open, setOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const { isMobile, isTablet } = useScreenSize();
  const isMobileOrTablet = isMobile || isTablet;
  const navigate = useNavigate();

  // Get user info from localStorage or context
  useEffect(() => {
    // TODO: Get avatar from user context/state when available
    const storedAvatar = localStorage.getItem('avatarUrl');
    if (storedAvatar) {
      setAvatarUrl(storedAvatar);
    }
  }, []);

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
    setOpen(false);
  };

  const handleLogoutConfirm = () => {
    // Emit logout to socket if available
    const socket = getSocket();
    if (socket && socket.connected) {
      try {
        socket.emit(C.LOGOUT_USER);
      } catch (error) {
        console.error('Error emitting logout:', error);
      }
    }

    // Clear all auth cookies
    Cookies.remove('token');
    Cookies.remove('uid');
    Cookies.remove('session');
    Cookies.remove('auth');

    // Clear all localStorage reliably
    try { localStorage.clear(); } catch (e) { /* silent */ }
    try { sessionStorage.clear(); } catch (e) { /* silent */ }

    // Close modals
    setShowLogoutModal(false);
    setOpen(false);

    // Hard redirect to home — don't use navigate() + reload() together
    window.location.href = '/';
  };

  const handleMenuItemClick = () => {
    setOpen(false);
  };

  const getValidAvatarUrl = (url) => {
    if (!url || typeof url !== 'string') return DEFAULT_AVATAR_PATH;
    if (url.startsWith('https') || url.startsWith('http') || url.startsWith('localhost')) {
      return DEFAULT_AVATAR_PATH;
    }
    return DEFAULT_AVATAR_PATH;
  };

  const menuItems = [
    { imageSrc: '/assets/images/new-profile/user.svg', label: t('profile.userInformation'), to: '/modal?mode=profile', action: handleMenuItemClick },
    { imageSrc: '/assets/images/new-profile/transactions.svg', label: t('profile.transactions'), to: '/modal?mode=bethistory&tab=transactions', action: handleMenuItemClick },
    { imageSrc: '/assets/images/new-profile/bet-history.svg', label: t('betHistory.betHistory'), to: '/modal?mode=bethistory&tab=bets', action: handleMenuItemClick },
    { imageSrc: '/assets/images/new-profile/wallet.svg', label: t('common.wallet'), to: '/wallet?mode=deposit', action: handleMenuItemClick },
    { imageSrc: '/assets/images/new-profile/swap.svg', label: t('common.swap'), to: '/wallet?mode=swap', action: handleMenuItemClick },
    { imageSrc: '/assets/images/newicons/p2p.svg', label: 'P2P', to: '/trade', action: handleMenuItemClick },
    { icon: shieldAlt, label: t('profile.vault'), to: '/wallet?mode=vault', action: handleMenuItemClick },
    { imageSrc: '/assets/images/new-profile/settings.svg', label: t('settings.globalSettings'), to: '/settings', action: handleMenuItemClick },
    { imageSrc: '/assets/images/newicons/provably.svg', label: t('profile.help'), to: '/provably-fair', action: handleMenuItemClick },
  ];

  // Mobile & tablet: navigate to /menu page
  if (isMobileOrTablet) {
    return (
      <button
        className={styles.profileButton}
        aria-label="Profile menu"
        onClick={() => navigate('/profile-menu')}
      >
        <img
          src={getValidAvatarUrl(avatarUrl)}
          alt="Profile"
          className={styles.profileImage}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = DEFAULT_AVATAR_PATH;
          }}
        />
        
      </button>
    );
  }

  // Desktop version with Popover
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          className={styles.profileButton}
          aria-label="Profile menu"
        >
          <img
            src={getValidAvatarUrl(avatarUrl)}
            alt="Profile"
            className={styles.profileImage}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = DEFAULT_AVATAR_PATH;
            }}
          />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className={styles.popoverContent}
          sideOffset={8}
          align="end"
          side="bottom"
          collisionPadding={16}
        >
          <div className={styles.menuList}>
            {menuItems.map((item, index) => (
              <Link
                key={index}
                to={item.to}
                className={styles.menuItem}
                onClick={item.action}
              >
                <div className={styles.iconWrapper}>
                  {item.icon ? (
                    <Icon icon={item.icon} width={20} height={20} style={{ color: '#D4AF37' }} />
                  ) : (
                    <img src={item.imageSrc} alt={item.label} />
                  )}
                </div>
                <span className={styles.menuText}>{item.label}</span>
              </Link>
            ))}

            <div className={styles.divider}></div>

            <button
              className={styles.menuItem}
              onClick={handleLogoutClick}
            >
              <div className={styles.iconWrapper}>
                <img src="/assets/images/new-profile/logout.svg" alt="Logout" />
              </div>
              <span className={styles.menuText}>{t('common.logout')}</span>
            </button>
          </div>

          <Popover.Arrow className={styles.popoverArrow} />
        </Popover.Content>
      </Popover.Portal>

      {/* Logout Confirmation Modal */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogoutConfirm}
      />
    </Popover.Root>
  );
};

export default ProfileMenu;
